import { INPUT_CLASS, LABEL_CLASS } from '@/components/common/node-view-settings-panel';
import { CommonNodeViewWrapper } from '@/components/common/node-view-wrapper';
import { ZyFullscreenToggle } from '@/components/tiptap-editor/tiptap-ui/zyfullscreen-toggle';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useIsEditorReadonly } from '@/hooks/use-is-editor-readonly';
import { useNodeViewCollaborationPresence } from '@/hooks/use-node-view-collaboration-presence';
import { createGdocDrawing, uploadGdocDrawingToS3 } from '@/lib/zybooks-api';
import type { ReactNodeViewProps } from '@tiptap/react';
import { OctagonAlertIcon, PencilIcon } from 'lucide-react';
import { useRef, useState } from 'react';
import { toast } from 'sonner';

const ZyGdocDrawingNodeView: React.FC<ReactNodeViewProps> = ({ node, updateAttributes, selected, editor, decorations }) => {
    const isEditorReadonly = useIsEditorReadonly(editor);
    const { subjectName, releaseId } = editor.state.doc.attrs;
    const { attrs } = node;
    const { id, gdoc_file_id } = attrs;
    const hasAlt = !!attrs.alt_text;
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [isCreatingGdocDrawing, setIsCreatingGdocDrawing] = useState(false);
    const [isUploadingGdocDrawing, setIsUploadingGdocDrawing] = useState(false);
    const [isHovering, setIsHovering] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const [altText, setAltText] = useState(attrs.alt_text);
    const [height, setHeight] = useState(attrs.height);
    const [width, setWidth] = useState(attrs.width);
    const [aspectRatio, setAspectRatio] = useState(attrs.aspect_ratio);
    const [pull, setPull] = useState(attrs.pull);
    const [imageId, setImageId] = useState(attrs.image_id);
    const [imagePreviewSrc, setImagePreviewSrc] = useState<string | undefined>(undefined);
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const imagePreviewRef = useRef<HTMLImageElement>(null);
    const { presenceUsers, presenceTracking } = useNodeViewCollaborationPresence(editor, id, 'zyGdocDrawing', isFullscreen);

    const refreshImageProperties = () => {
        const { alt_text, height, width, pull, image_id } = attrs;
        setAltText(alt_text);
        setHeight(height);
        setWidth(width);
        setPull(pull);
        setImageId(image_id);
    };

    const createGdocDrawingById = async () => {
        setIsCreatingGdocDrawing(true);
        try {
            const gdocFileId = await createGdocDrawing(subjectName, releaseId);
            updateAttributes({
                subject: editor.state.doc.attrs.uploadImagesToMATESTBucket ? 'MATEST' : editor.state.doc.attrs.subjectName,
                release_id: editor.state.doc.attrs.uploadImagesToMATESTBucket ? 'MATEST' : editor.state.doc.attrs.releaseId,
                gdoc_file_id: gdocFileId,
            });
            setIsCreatingGdocDrawing(false);
        } catch (error) {
            console.error('Error creating Gdoc Drawing', error);
            setIsCreatingGdocDrawing(false);
            toast.error('Error creating Gdoc Drawing', {
                description: (error as Error).message,
            });
        }
    };

    const uploadImageToS3 = async () => {
        setIsUploadingGdocDrawing(true);
        try {
            const { key, last_modified } = await uploadGdocDrawingToS3(gdoc_file_id, id, subjectName, releaseId, attrs.gdoc_modified_time);
            const imageIdFromS3 = key.split('/').pop() || '';
            setImageId(imageIdFromS3);
            updateAttributes({ image_id: imageIdFromS3, gdoc_modified_time: last_modified });
            const cacheBust = new Date().getTime();
            const imagePreviewSrc = `https://zytools.zybooks.com/zyAuthor/${subjectName}/${releaseId}/IMAGES/${imageIdFromS3}?cache-bust=${cacheBust}`;
            setImagePreviewSrc(imagePreviewSrc);
            setIsUploadingGdocDrawing(false);
        } catch (error) {
            console.error('Error uploading Gdoc Drawing to S3', error);
            setIsUploadingGdocDrawing(false);
            toast.error('Error uploading Gdoc Drawing to S3', {
                description: (error as Error).message,
            });
        }
    };

    const commitDefaultDimensionsIfNeeded = () => {
        const clientWidth = imagePreviewRef.current?.clientWidth;
        const clientHeight = imagePreviewRef.current?.clientHeight;
        const clientWidthAndHeightAreValid = clientWidth && clientHeight && clientWidth > 0 && clientHeight > 0;
        if (clientWidthAndHeightAreValid) {
            const aspectRatio = clientWidth / clientHeight;
            setWidth(clientWidth);
            setHeight(clientHeight);
            setAspectRatio(aspectRatio);
            updateAttributes({ width: clientWidth, height: clientHeight, aspect_ratio: aspectRatio });
        }
    };

    const getGdocDrawingIframeSrc = () => {
        return gdoc_file_id ? `https://docs.google.com/drawings/d/${gdoc_file_id}/edit` : '';
    };

    const updateHeight = (newHeight: number) => {
        // Also update the width to keep the aspect ratio
        const newWidth = Math.round(aspectRatio * newHeight);

        setWidth(newWidth);
        setHeight(newHeight);
    };

    const updateWidth = (newWidth: number) => {
        // Also update the height to keep the aspect ratio
        const newHeight = Math.round(newWidth / aspectRatio);

        setHeight(newHeight);
        setWidth(newWidth);
    };

    const updateImageAttributes = () => {
        updateAttributes({ alt_text: altText, width: width, height: height, pull: pull, image_id: imageId });
    };

    return (
        <CommonNodeViewWrapper
            decorations={decorations}
            label={isFullscreen ? null : 'Gdoc Drawing'}
            selected={selected}
            containerClassName={`${isFullscreen ? 'inset-0 z-51 rounded-none' : ''}`}
            contentClassName="mx-0"
            position={isFullscreen ? 'fixed' : 'relative'}
            presenceTracking={presenceTracking}
            presenceUsers={presenceUsers}
        >
            <div
                className="border-b border-gray-200 dark:border-gray-700 min-h-96"
                onMouseEnter={() => setIsHovering(true)}
                onMouseLeave={() => setIsHovering(false)}
            >
                <div className="bg-gray-100 px-4 py-2 dark:bg-gray-800 flex items-center justify-end border-b gap-x-2">
                    <ZyFullscreenToggle
                        isFullscreen={isFullscreen}
                        setIsFullscreen={setIsFullscreen}
                    />
                </div>
                {(isHovering || isUploadingGdocDrawing) && gdoc_file_id && (
                    <div
                        className={`absolute top-14 right-2 z-10 max-w-100 bg-gray-100 rounded-md flex items-center gap-1 px-2 ${hasAlt ? 'border border-gray-300' : 'border border-red-500'}`}
                    >
                        <Button
                            variant="ghost"
                            size="icon"
                            className="cursor-pointer"
                            disabled={isEditorReadonly}
                            isLoading={isUploadingGdocDrawing}
                            onClick={async () => {
                                refreshImageProperties();
                                await uploadImageToS3();
                                setIsOpen(true);
                            }}
                        >
                            <PencilIcon className="size-4" />
                        </Button>
                        <span className="text-xs">Alt text: {attrs.alt_text || 'No alt text'}</span>
                    </div>
                )}
                {!isHovering && gdoc_file_id && !hasAlt && !isUploadingGdocDrawing && (
                    <div className="absolute top-14 right-2 z-10 max-w-100 bg-gray-100 rounded-md flex items-center gap-1 p-2 border border-red-500">
                        <OctagonAlertIcon className="size-8 text-red-500" />
                        <span className="text-xs">No alt text</span>
                    </div>
                )}
                <div
                    className={`flex items-center align-middle justify-center bg-white dark:bg-gray-900 ${isFullscreen ? 'h-[calc(100vh-53px)]' : 'h-96'}`}
                >
                    {gdoc_file_id ? (
                        <iframe
                            ref={iframeRef}
                            src={getGdocDrawingIframeSrc()}
                            className="h-full w-full border-0"
                            title="Gdoc Drawing"
                        />
                    ) : (
                        <Button
                            onClick={createGdocDrawingById}
                            disabled={isEditorReadonly}
                            isLoading={isCreatingGdocDrawing}
                        >
                            Create Gdoc Drawing
                        </Button>
                    )}
                </div>
            </div>
            <Dialog
                open={isOpen}
                onOpenChange={setIsOpen}
            >
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Edit Image</DialogTitle>
                    </DialogHeader>
                    <div className="image-preview flex items-center justify-center">
                        <img
                            src={imagePreviewSrc}
                            alt={attrs.alt_text}
                            width={attrs.width}
                            height={attrs.height}
                            ref={imagePreviewRef}
                            onLoad={commitDefaultDimensionsIfNeeded}
                        />
                    </div>
                    <fieldset
                        disabled={isEditorReadonly}
                        className="m-0 min-w-0 border-0 p-0"
                    >
                        <div className="flex flex-col gap-2">
                            <label className={LABEL_CLASS}>
                                Alt Text
                                <Textarea
                                    id="alt"
                                    className={`${INPUT_CLASS} ${altText ? '' : 'border-2 border-red-500'}`}
                                    value={altText ?? ''}
                                    onChange={e => setAltText(e.target.value === '' ? null : e.target.value)}
                                    required
                                />
                            </label>
                        </div>

                        <div className="flex flex-row gap-2">
                            <div className="flex flex-col flex-grow gap-2">
                                <label className={LABEL_CLASS}>
                                    Height
                                    <Input
                                        id="height"
                                        className={INPUT_CLASS}
                                        type="number"
                                        value={height}
                                        onChange={e => updateHeight(Number(e.target.value))}
                                    />
                                </label>
                            </div>
                            <div className="flex flex-col flex-grow gap-2">
                                <label className={LABEL_CLASS}>
                                    Width
                                    <Input
                                        id="width"
                                        className={INPUT_CLASS}
                                        type="number"
                                        value={width}
                                        onChange={e => updateWidth(Number(e.target.value))}
                                    />
                                </label>
                            </div>
                        </div>
                        <div className="flex flex-col gap-2">
                            <label className={LABEL_CLASS}>
                                Pull
                                <select
                                    id="pull"
                                    className={INPUT_CLASS}
                                    value={pull ?? 'none'}
                                    onChange={e => setPull(e.target.value === 'none' ? null : e.target.value)}
                                >
                                    <option value="none">None</option>
                                    <option value="left">Left</option>
                                    <option value="right">Right</option>
                                </select>
                            </label>
                        </div>
                    </fieldset>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => {
                                setIsOpen(false);
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            disabled={isEditorReadonly}
                            onClick={() => {
                                updateImageAttributes();
                                setIsOpen(false);
                            }}
                        >
                            Save
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </CommonNodeViewWrapper>
    );
};

export default ZyGdocDrawingNodeView;
